import { useEffect, useRef, useState } from 'react'

// Minimap: one pixel per region centroid, prerendered to an offscreen canvas
// once per regionsData load, with a viewport rectangle redrawn on pan/zoom.
// Shown only once zoomed past the point where the main view stops showing the
// whole world. Returns the canvas ref, visibility, and click-to-recenter handler.
export function useMinimap(map, regionsData, background) {
  const minimapCanvasRef = useRef(null)
  const minimapStaticRef = useRef(null) // offscreen canvas, redrawn only when regionsData changes
  const minimapBoundsRef = useRef(null) // { minLng, maxLng, minLat, maxLat } — shifted lng space
  const [minimapVisible, setMinimapVisible] = useState(false)

  // Static layer: prerender centroids to the offscreen canvas.
  useEffect(() => {
    if (!regionsData?.features?.length) return
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity
    const points = []
    regionsData.features.forEach((f) => {
      const c = f.properties._centroid
      if (!c) return
      const [lng, lat] = c
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      points.push({ lng, lat, color: f.properties.country_color || '#4a4a4a' })
    })
    if (!points.length) return
    minimapBoundsRef.current = { minLng, maxLng, minLat, maxLat }

    const W = 180, H = 110
    const off = document.createElement('canvas')
    off.width = W
    off.height = H
    const ctx = off.getContext('2d')
    ctx.fillStyle = background || '#0d0d0d'
    ctx.fillRect(0, 0, W, H)
    const spanLng = (maxLng - minLng) || 1
    const spanLat = (maxLat - minLat) || 1
    points.forEach(({ lng, lat, color }) => {
      const x = ((lng - minLng) / spanLng) * W
      const y = H - ((lat - minLat) / spanLat) * H
      ctx.fillStyle = color
      ctx.fillRect(x, y, 1.5, 1.5)
    })
    minimapStaticRef.current = off
  }, [regionsData, background])

  // Viewport rectangle + visibility, redrawn on move/zoom.
  useEffect(() => {
    if (!map.current) return
    const MINIMAP_ZOOM_THRESHOLD = 3.2

    const redraw = () => {
      const canvas = minimapCanvasRef.current
      const off = minimapStaticRef.current
      const bounds = minimapBoundsRef.current
      const zoom = map.current.getZoom()
      const visible = zoom > MINIMAP_ZOOM_THRESHOLD
      setMinimapVisible(visible)
      if (!visible || !canvas || !off || !bounds) return

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(off, 0, 0)

      const b = map.current.getBounds()
      const { minLng, maxLng, minLat, maxLat } = bounds
      const spanLng = (maxLng - minLng) || 1
      const spanLat = (maxLat - minLat) || 1
      const project = (lng, lat) => [
        ((lng - minLng) / spanLng) * canvas.width,
        canvas.height - ((lat - minLat) / spanLat) * canvas.height,
      ]
      const [x1, y1] = project(b.getWest(), b.getNorth())
      const [x2, y2] = project(b.getEast(), b.getSouth())
      ctx.strokeStyle = '#D5B654'
      ctx.lineWidth = 1.5
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1))
    }

    map.current.on('move', redraw)
    map.current.on('zoom', redraw)
    redraw()
    return () => {
      map.current?.off('move', redraw)
      map.current?.off('zoom', redraw)
    }
  }, [map, regionsData])

  const handleMinimapClick = (e) => {
    const canvas = minimapCanvasRef.current
    const bounds = minimapBoundsRef.current
    if (!canvas || !bounds || !map.current) return
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const { minLng, maxLng, minLat, maxLat } = bounds
    const lng = minLng + (x / canvas.width) * (maxLng - minLng)
    const lat = maxLat - (y / canvas.height) * (maxLat - minLat)
    map.current.easeTo({ center: [lng, lat], duration: 400 })
  }

  return { minimapCanvasRef, minimapVisible, handleMinimapClick }
}
